/**
 * Which one-place events this trainer is attending (2026-10-23 owner ask).
 *
 * A City Safari in Boston or a Wild Area weekend in Sendai is noise for
 * everyone who is not there, so the Worker keeps such an event out of "Now"
 * and "Upcoming" (and the Profile radar, the Today card, the catch-up prompt:
 * every surface that reads /api/events/live) unless the device says it is
 * attending. The Calendar lists them under "In person" with a switch; this
 * module is the switch's memory.
 *
 * Device-local first (localStorage, like the event reminders), because the
 * live feed is public and unauthenticated: the ids ride on the request as
 * `?attending=`, see eventsLiveKey. Synced to the account best-effort
 * (PUT /api/events/:id/attending) for the signed-in surfaces: a failed sync
 * never blocks the toggle, and signing in pulls the account's list down.
 */
import { useSyncExternalStore } from "react";

// The query client is loaded on demand: this module sits under
// eventsLiveKey, which every live-feed reader imports, and pulling
// @tanstack/react-query in at module scope dragged it into tests that mock
// that package down to useQuery (activeBonusesOverlayHookOrder, 2026-10-23).
async function api(): Promise<typeof import("./queryClient")> {
  return import("./queryClient");
}

const STORAGE_KEY = "nexus.eventAttending.v1";
export const EVENT_ATTENDING_EVENT = "nexus:event-attending-changed";

let cache: Set<string> | null = null;
/** One frozen array per state so useSyncExternalStore sees a stable snapshot. */
let snapshot: readonly string[] | null = null;

function readSet(): Set<string> {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    cache = new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : []);
  } catch {
    cache = new Set();
  }
  return cache;
}

function persist(): void {
  snapshot = null;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...readSet()]));
  } catch {
    /* private mode: the session still knows */
  }
  try {
    window.dispatchEvent(new Event(EVENT_ATTENDING_EVENT));
  } catch {
    /* no window */
  }
}

/** The attended ids, sorted, as one stable array per change. */
export function attendingIds(): readonly string[] {
  if (!snapshot) snapshot = Object.freeze([...readSet()].sort());
  return snapshot;
}

export function isAttending(eventId: string): boolean {
  return readSet().has(eventId);
}

/**
 * Flip attendance for one event. The device remembers at once; the account
 * copy follows when signed in, and a failure there is logged, not surfaced:
 * the switch has already done the thing the trainer sees.
 */
export function setAttending(eventId: string, attending: boolean): void {
  const set = readSet();
  if (attending) set.add(eventId);
  else set.delete(eventId);
  persist();
  void api()
    .then(({ apiRequest, hasAuthToken }) => {
      if (!hasAuthToken()) return;
      return apiRequest("PUT", `/api/events/${encodeURIComponent(eventId)}/attending`, { attending });
    })
    .catch((err) => {
      console.warn("[eventAttending] sync failed", err);
    });
}

export function toggleAttending(eventId: string): boolean {
  const next = !isAttending(eventId);
  setAttending(eventId, next);
  return next;
}

/**
 * Pull the account's list down (after sign-in, or on a fresh device) and
 * union it with what this device already knows. Never removes a local id:
 * the device may have toggled while offline and not synced yet.
 */
export async function hydrateAttendingFromAccount(): Promise<void> {
  const { apiRequest, hasAuthToken } = await api();
  if (!hasAuthToken()) return;
  try {
    const res = await apiRequest("GET", "/api/events/attending");
    const body = (await res.json()) as { eventIds?: unknown };
    const ids = Array.isArray(body.eventIds) ? body.eventIds.filter((v): v is string => typeof v === "string") : [];
    if (ids.length === 0) return;
    const set = readSet();
    let changed = false;
    for (const id of ids) {
      if (!set.has(id)) {
        set.add(id);
        changed = true;
      }
    }
    if (changed) persist();
  } catch {
    /* offline or signed out: the local list stands */
  }
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(EVENT_ATTENDING_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(EVENT_ATTENDING_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

/** The attended ids, live: re-renders the caller when the switch is flipped anywhere. */
export function useAttendingIds(): readonly string[] {
  return useSyncExternalStore(subscribe, attendingIds, attendingIds);
}

/** Tests only. */
export function __resetEventAttendingForTest(): void {
  cache = null;
  snapshot = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
